import dayjs from "dayjs";
import {
	ArrowDownToLine,
	ArrowUpToLine,
	CalendarDays,
	CircleHelp,
	Clock,
	LocateFixed,
	Redo2,
	Undo2,
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import LoadingIndicator from "@/components/Shared/LoadingIndicator/LoadingIndicator";
import ShiftPlanner, {
	ShiftPlannerConsultant,
	ShiftPlannerHandle,
	ShiftPlannerShift,
	diffShifts,
	findConsultantConflict,
	plannerSummary,
	useShiftPlannerHistory,
} from "@/components/Shared/ShiftPlanner";
import { SIDE_MENU_WIDTH } from "@/components/Shared/SideMenu/SideMenu";
import { TIME_REPORT_STATUS } from "@/configs/constants/TimeReportStatus";
import { routes } from "@/configs/constants/routes";
import { useModalStore } from "@/hooks/modal.hooks";
import Job from "@/models/Job";
import { useJobShifts, useMatchingConsultants, useUpdateShifts } from "@/networking/job.hooks";
import { useTimeReports } from "@/networking/time-report.hooks";
import { formatNumber } from "@/utils/date.time.utils";
import JobConsultantPanel from "./JobConsultantPanel";
import JobTimeReportsPanel from "./JobTimeReportsPanel";
import {
	addsInvitations,
	plannerShiftStartsInPast,
	toPlannerConsultants,
	toPlannerShifts,
	toUpdateRows,
} from "./jobMatching.utils";

/** Must match the fixed JobHeader width in JobDetailsPage. */
const JOB_HEADER_WIDTH = 310;

/**
 * Router location state the Kandidater tab navigates here with — consumed
 * once after the planner loads, then cleared from history.
 */
export interface ScheduleNavigationState {
	/** Scroll the planner to this date (YYYY-MM-DD). */
	scrollToDate?: string;
}

/** A compact toolbar action: icon + label, ghost until hovered. */
/** The side menu's official "Tidrapporter" clock (icons/time-icon.svg) as a
 * currentColor variant — the shared asset hardcodes a white stroke and a
 * fixed size, so it can't be reused directly in the toolbar. */
const TimeClockIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.8}
		strokeLinecap="round"
		strokeLinejoin="round"
		{...props}
	>
		<circle cx="12" cy="12" r="10" />
		<line x1="12" y1="2" x2="12" y2="5" />
		<line x1="22" y1="12" x2="19" y2="12" />
		<line x1="12" y1="22" x2="12" y2="19" />
		<line x1="2" y1="12" x2="5" y2="12" />
		<polyline points="8 6 12 12 16 12" />
	</svg>
);

const ToolbarButton = React.forwardRef<
	HTMLButtonElement,
	React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: React.ElementType }
>(function ToolbarButton({ icon: Icon, children, ...props }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			{...props}
			className="flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
		>
			<Icon className="h-4 w-4 text-gray-500" />
			{children}
		</button>
	);
});

const ToolbarDivider = () => <div className="mx-1 h-6 w-px shrink-0 bg-gray-200" />;

/** A read-only figure in the toolbar, e.g. total days/hours. */
const ToolbarStat: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({
	icon: Icon,
	label,
	value,
}) => (
	<span className="flex items-center gap-1.5 px-2.5 text-sm text-gray-500">
		<Icon className="h-4 w-4" />
		<span className="font-semibold tabular-nums text-gray-900">{value}</span>
		{label}
	</span>
);

/**
 * The consolidated "Matchning" tab — the ShiftPlanner week grid wired to the
 * real job: server shifts/applications/invitations map to the planner view
 * model, edits are drafted locally with undo/redo, and saving replaces the
 * job's shifts via PUT /job/:id/shifts (same endpoint and semantics as the
 * legacy shifts tab). Booking and inviting consultants goes through the
 * {@link JobConsultantPanel} floating panel; its actions are draft edits too.
 */
const JobMatchingTab: React.FC<{ job: Job }> = ({ job }) => {
	const { t } = useTranslation();

	const { data: jobShifts } = useJobShifts({
		jobId: job.id,
		status: ["NOT_SET", "EXTEND_REQUESTED", "EXTEND_DECLINED", "EXTEND_ACCEPTED"],
		include: ["applications"],
	});
	const { data: timeReports } = useTimeReports({ job: job.id, expand: "consultant,shift" });
	const { mutateAsync: updateShifts, isLoading: isSaving } = useUpdateShifts();

	// Matching consultants for the planner's right-click book/invite menu —
	// same source as the consultant panel. The backend defaults to 20 rows;
	// ask for the same 300 as the Kandidater tab so the submenu search
	// actually covers the whole match set.
	const { data: matchingUsers } = useMatchingConsultants({ jobId: job.id, pageSize: 300 });
	const matchingConsultants = useMemo(
		() =>
			(matchingUsers ?? [])
				.filter((user) => !user.blocked)
				.map((user) => ({
					id: user.id,
					name: `${user.firstName} ${user.lastName}`,
					// The submenu search matches on it besides the name
					employeeNumber: user.profile?.employeeNumber,
				}))
				.sort((a, b) => a.name.localeCompare(b.name, "sv")),
		[matchingUsers]
	);

	// The consultant panel: null = closed; shiftIds = the draft shifts being
	// staffed (empty = no specific shifts, the panel opens in candidates view).
	const [picker, setPicker] = useState<{ shiftIds: string[] } | null>(null);

	// Consultants booked via the panel this session — not yet in the server
	// roster but needed for row labels and invite-name lookups.
	const [extraConsultants, setExtraConsultants] = useState<ShiftPlannerConsultant[]>([]);

	// The last saved server state (view-model) the diff/preview compares against.
	const [baseline, setBaseline] = useState<ShiftPlannerShift[]>();
	const { shifts, setShifts, undo, redo, canUndo, canRedo, reset } = useShiftPlannerHistory();

	// Sidebar callbacks live across renders — always act on the current draft.
	const draftRef = useRef<ShiftPlannerShift[]>([]);
	draftRef.current = shifts;

	const plannerRef = useRef<ShiftPlannerHandle>(null);

	// The global absence modal (rendered by LoggedInLayout) — the booked-pill
	// context menu's "Registrera frånvaro" opens it for the shift's consultant.
	const toggleAbsenceModal = useModalStore((state) => state.toggleAbsenceModal);

	const absentShiftIds = useMemo(
		() =>
			new Set(
				(timeReports?.timeReports || [])
					.filter((report) => !!report.absence)
					.map((report) => report.shift.id || "")
			),
		[timeReports]
	);

	const mapped = useMemo(
		() => (jobShifts ? toPlannerShifts(jobShifts, absentShiftIds) : undefined),
		[jobShifts, absentShiftIds]
	);

	const diff = diffShifts(baseline || [], shifts);
	const changeCount = diff.created.length + diff.updated.length + diff.removed.length;
	const hasChanges = !!baseline && changeCount > 0;

	// Seed the draft from the server, and re-seed on refetches (e.g. right
	// after a save, when created shifts get their real ids) — but never
	// clobber unsaved local edits.
	useEffect(() => {
		if (!mapped || hasChanges) return;
		setBaseline(mapped);
		reset(mapped);
	}, [mapped]); // eslint-disable-line react-hooks/exhaustive-deps

	const consultants = useMemo(() => {
		const roster = jobShifts ? toPlannerConsultants(jobShifts) : [];
		const merged = [
			...roster,
			...extraConsultants.filter((extra) => !roster.some((c) => c.id === extra.id)),
		];
		return merged.sort((a, b) => a.name.localeCompare(b.name, "sv"));
	}, [jobShifts, extraConsultants]);

	// id → name for everyone we can know about (roster, applicants, invitees) —
	// used to show names for invitations added in this draft.
	const consultantNameById = useMemo(() => {
		const map = new Map<string, string>();
		for (const shift of jobShifts || []) {
			for (const application of shift.applications || []) {
				map.set(
					application.consultant.id,
					`${application.consultant.firstName} ${application.consultant.lastName}`
				);
			}
			for (const invitation of shift.invitations || []) {
				map.set(
					invitation.consultant.id,
					`${invitation.consultant.firstName} ${invitation.consultant.lastName}`
				);
			}
		}
		for (const consultant of consultants) map.set(consultant.id, consultant.name);
		return map;
	}, [jobShifts, consultants]);

	function applyBooking(targetIds: string[], consultant: ShiftPlannerConsultant) {
		// Never book a consultant onto overlapping shifts — check the would-be
		// result (their existing shifts + the targets booked to them)
		const wouldBe = draftRef.current.map((shift) =>
			targetIds.includes(shift.id) && !shift.absence
				? { ...shift, consultantId: consultant.id }
				: shift
		);
		const conflict = findConsultantConflict(
			wouldBe,
			wouldBe.filter((shift) => targetIds.includes(shift.id))
		);
		if (conflict) {
			window.alert(
				t("shiftPlanner.overlapAlert", {
					name: consultant.name,
					date: conflict.conflicting.date,
					times: `${conflict.conflicting.startTime} - ${conflict.conflicting.endTime}`,
				})
			);
			return;
		}
		setExtraConsultants((prev) =>
			prev.some((c) => c.id === consultant.id) ? prev : [...prev, consultant]
		);
		setShifts(
			draftRef.current.map((shift) => {
				if (!targetIds.includes(shift.id) || shift.absence) return shift;
				// Booking replaces any pending invitations, like the legacy calendar
				const next: ShiftPlannerShift = {
					...shift,
					consultantId: consultant.id,
					invitedConsultantIds: [],
					invitedConsultantNames: [],
				};
				// ...and settles the consultant's own application, so the pending
				// badge drops them right away (the refetch after save does it for real)
				if (shift.appliedConsultantIds?.includes(consultant.id)) {
					const kept = shift.appliedConsultantIds
						.map((id, index) => (id !== consultant.id ? index : -1))
						.filter((index) => index >= 0);
					next.appliedConsultantIds = kept.map((i) => shift.appliedConsultantIds![i]);
					next.appliedConsultantNames = kept.map((i) => shift.appliedConsultantNames?.[i] ?? "");
				}
				return next;
			})
		);
	}

	function applyUnbook(targetIds: string[], consultantId: string) {
		setShifts(
			draftRef.current.map((shift) =>
				targetIds.includes(shift.id) && shift.consultantId === consultantId && !shift.absence
					? { ...shift, consultantId: undefined }
					: shift
			)
		);
	}

	function applyInvites(targetIds: string[], consultantIds: string[]) {
		const names = consultantIds.map((id) => consultantNameById.get(id) ?? t("CONSULTANT"));
		setShifts(
			draftRef.current.map((shift) => {
				if (!targetIds.includes(shift.id) || shift.absence) return shift;
				return {
					...shift,
					invitedConsultantIds: consultantIds,
					invitedConsultantNames: names,
					// Inviting unbooks, like the legacy calendar (the panel confirms first)
					consultantId: consultantIds.length ? undefined : shift.consultantId,
				};
			})
		);
	}

	/** Drop every draft invitation of one consultant, on any shift. */
	/** Context-menu "Önska" — ADD one consultant's invitation to the target
	 * shifts, keeping any invitations already there (applyInvites replaces the
	 * whole list — that's the panel's semantics, not the menu's). */
	function applyInviteAdd(consultant: ShiftPlannerConsultant, shiftIds: string[]) {
		setExtraConsultants((prev) =>
			prev.some((c) => c.id === consultant.id) ? prev : [...prev, consultant]
		);
		setShifts(
			draftRef.current.map((shift) => {
				if (
					!shiftIds.includes(shift.id) ||
					shift.absence ||
					shift.invitedConsultantIds?.includes(consultant.id)
				) {
					return shift;
				}
				return {
					...shift,
					invitedConsultantIds: [...(shift.invitedConsultantIds ?? []), consultant.id],
					invitedConsultantNames: [...(shift.invitedConsultantNames ?? []), consultant.name],
				};
			})
		);
	}

	function applyUninvite(consultantId: string) {
		setShifts(
			draftRef.current.map((shift) => {
				if (!shift.invitedConsultantIds?.includes(consultantId)) return shift;
				const keptIndexes = shift.invitedConsultantIds
					.map((id, index) => (id !== consultantId ? index : -1))
					.filter((index) => index >= 0);
				return {
					...shift,
					invitedConsultantIds: keptIndexes.map((i) => shift.invitedConsultantIds![i]),
					invitedConsultantNames: keptIndexes.map(
						(i) => shift.invitedConsultantNames?.[i] ?? t("CONSULTANT")
					),
				};
			})
		);
	}

	function handleRequestConsultantPicker(shiftIds: string[]) {
		const targets = draftRef.current.filter((shift) => shiftIds.includes(shift.id));
		if (targets.length === 0) return;
		setPicker({ shiftIds });
	}

	// The Kandidater tab navigates here with instructions in the location state
	// ("show these shifts") — act on them once the planner has loaded, then
	// clear the state so refresh/back don't replay it.
	const location = useLocation();
	const navigate = useNavigate();
	const navStateConsumed = useRef(false);
	const loaded = !!jobShifts && !!baseline;
	useEffect(() => {
		const state = location.state as ScheduleNavigationState | null;
		if (navStateConsumed.current || !loaded || !state) return;
		navStateConsumed.current = true;

		if (state.scrollToDate) plannerRef.current?.scrollToDate(state.scrollToDate);
		// Re-navigate to the EXPLICIT current path — inside the /jobs/:jobId/*
		// splat route a relative "." resolves without the tab segment and would
		// bounce the page back to the overview tab.
		navigate(location.pathname + location.search, { replace: true, state: null });
	}, [loaded, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

	const errors = useMemo(() => {
		if (!hasChanges) return [];
		const result: string[] = [];
		if (shifts.length === 0) result.push(t("job.shifts.errors.noShifts"));

		const beforeById = new Map((baseline || []).map((shift) => [shift.id, shift]));
		const invitesPastShift = shifts.some(
			(shift) => plannerShiftStartsInPast(shift) && addsInvitations(shift, beforeById.get(shift.id))
		);
		if (invitesPastShift) result.push(t("job.shifts.errors.pastInvitations"));
		return result;
	}, [hasChanges, shifts, baseline, t]);

	const warnings = useMemo(() => {
		if (!hasChanges || !timeReports) return [];
		const result: string[] = [];

		const removedIds = new Set(diff.removed.map((shift) => shift.id));
		const unbookedIds = new Set(
			diff.updated
				.filter(
					({ before, after }) => before.consultantId && after.consultantId !== before.consultantId
				)
				.map(({ after }) => after.id)
		);

		const absenceTouched = timeReports.timeReports.some(
			(report) => !!report.absence && removedIds.has(report.shift.id || "")
		);
		if (absenceTouched) result.push(t("job.shifts.warnings.absenceModified"));

		const reportedTouched = timeReports.timeReports.some(
			(report) =>
				report.status === TIME_REPORT_STATUS.WORKED &&
				(removedIds.has(report.shift.id || "") || unbookedIds.has(report.shift.id || ""))
		);
		if (reportedTouched) result.push(t("job.shifts.warnings.reportedTimeReportsModified"));

		// Time edits on shifts that already have a reported time report
		const timeEditedIds = new Set(
			diff.updated
				.filter(
					({ before, after }) =>
						before.date !== after.date ||
						before.startTime !== after.startTime ||
						before.endTime !== after.endTime ||
						(before.breakHours || 0) !== (after.breakHours || 0)
				)
				.map(({ after }) => after.id)
		);
		const reportedTimeEdited = timeReports.timeReports.some(
			(report) =>
				report.status === TIME_REPORT_STATUS.WORKED && timeEditedIds.has(report.shift.id || "")
		);
		if (reportedTimeEdited) result.push(t("shiftPlanner.reportedTimeEdited"));

		return result;
	}, [hasChanges, diff, timeReports, t]);

	async function handleSave() {
		setPicker(null);
		const createdIds = new Set(diff.created.map((shift) => shift.id));
		await updateShifts({ jobId: job.id, shifts: toUpdateRows(shifts, createdIds) });
		// Optimistic new baseline; the invalidated refetch re-seeds with the
		// server truth (real ids for created shifts) via the effect above.
		setBaseline(shifts);
		reset();
	}

	// Fill the screen: the planner gets every pixel below its top edge, minus a
	// small page margin — or room for the fixed save bar while it is shown.
	const plannerWrapRef = useRef<HTMLDivElement>(null);
	const [plannerHeight, setPlannerHeight] = useState<number>();
	useLayoutEffect(() => {
		function update() {
			const el = plannerWrapRef.current;
			if (!el) return;
			const bottomSpace = hasChanges ? 96 : 16;
			setPlannerHeight(
				Math.max(320, window.innerHeight - el.getBoundingClientRect().top - bottomSpace)
			);
		}
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, [loaded, hasChanges]);

	if (!jobShifts || !baseline) return <LoadingIndicator />;

	const summary = plannerSummary(shifts);
	const sortedDates = shifts.map((shift) => shift.date).sort();
	const firstShiftDate = sortedDates[0];
	const lastShiftDate = sortedDates[sortedDates.length - 1];
	const timeReportsHref = routes.TIME_REPORTS + `?job=${JSON.stringify({ key: job.id })}`;

	return (
		<div className="mx-auto mb-4 mt-4 flex max-w-[1500px] flex-col gap-4">
			{/* Planner toolbar: quick navigation, totals and the time reports panel */}
			<div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 shadow-sm">
				<ToolbarButton
					icon={ArrowUpToLine}
					disabled={!firstShiftDate}
					onClick={() => firstShiftDate && plannerRef.current?.scrollToDate(firstShiftDate)}
				>
					{t("shiftPlanner.firstShift")}
				</ToolbarButton>
				<ToolbarButton
					icon={LocateFixed}
					onClick={() => plannerRef.current?.scrollToDate(dayjs().format("YYYY-MM-DD"))}
				>
					{t("shiftPlanner.today")}
				</ToolbarButton>
				<ToolbarButton
					icon={ArrowDownToLine}
					disabled={!lastShiftDate}
					onClick={() => lastShiftDate && plannerRef.current?.scrollToDate(lastShiftDate)}
				>
					{t("shiftPlanner.lastShift")}
				</ToolbarButton>

				<ToolbarDivider />

				<ToolbarStat
					icon={CalendarDays}
					value={String(summary.days)}
					label={t("shiftPlanner.daysUnit")}
				/>
				<ToolbarStat
					icon={Clock}
					value={formatNumber(summary.hours, 0)}
					label={t("shiftPlanner.hoursUnit")}
				/>

				<div className="ml-auto" />

				<JobTimeReportsPanel
					timeReports={timeReports?.timeReports || []}
					timeReportsHref={timeReportsHref}
					trigger={
						<ToolbarButton icon={TimeClockIcon}>
							{t("shiftPlanner.timeReports")}
							{(timeReports?.timeReports.length || 0) > 0 && (
								<span className="rounded-full bg-gray-100 px-1.5 text-xs tabular-nums text-gray-600">
									{timeReports?.timeReports.length}
								</span>
							)}
						</ToolbarButton>
					}
				/>

				<KnowledgeBaseTrigger tag="shift-planner">
					<ToolbarButton icon={CircleHelp}>{t("shiftPlanner.help")}</ToolbarButton>
				</KnowledgeBaseTrigger>
			</div>

			<div ref={plannerWrapRef} style={plannerHeight ? { height: plannerHeight } : undefined}>
				<ShiftPlanner
					ref={plannerRef}
					shifts={shifts}
					consultants={consultants}
					onChange={setShifts}
					onRequestConsultantPicker={handleRequestConsultantPicker}
					onAcceptApplication={(consultant, shiftIds) => applyBooking(shiftIds, consultant)}
					onRemoveInvitation={applyUninvite}
					matchingConsultants={matchingConsultants}
					onAssignConsultant={(consultant, shiftIds) => applyBooking(shiftIds, consultant)}
					onInviteConsultant={applyInviteAdd}
					onReportAbsence={(consultantId, date) =>
						toggleAbsenceModal({
							consultantId,
							title: consultantNameById.get(consultantId),
							selectedDate: date,
						})
					}
					className="h-full max-h-full"
				/>
			</div>

			<JobConsultantPanel
				job={job}
				open={!!picker}
				onOpenChange={(open) => !open && setPicker(null)}
				targetShiftIds={picker?.shiftIds ?? []}
				draftShifts={shifts}
				serverShifts={jobShifts}
				consultantNameById={consultantNameById}
				onBook={(consultant, shiftIds) => {
					applyBooking(shiftIds, consultant);
					// The consultant is applied — the selection highlight that showed
					// the picker's target slots has served its purpose
					plannerRef.current?.clearSelection();
				}}
				onUnbook={(consultantId, shiftIds) => {
					applyUnbook(shiftIds, consultantId);
					plannerRef.current?.clearSelection();
				}}
				onInviteChange={(consultantIds) => picker && applyInvites(picker.shiftIds, consultantIds)}
				onUninvite={applyUninvite}
			/>

			{hasChanges && (
				<div
					className="fixed bottom-0 right-0 z-[102] flex min-h-[80px] items-center justify-end gap-4 border-t border-gray-200 bg-white px-10 py-4"
					style={{ width: `calc(100% - ${JOB_HEADER_WIDTH + SIDE_MENU_WIDTH}px)` }}
				>
					{errors.length > 0 && (
						<div className="flex flex-col gap-1">
							{errors.map((error) => (
								<p key={error} className="text-sm text-danger">
									{error}
								</p>
							))}
						</div>
					)}
					{errors.length === 0 && warnings.length > 0 && (
						<div className="flex max-w-[400px] flex-col gap-0.5 border border-orange-500 bg-gray-50 px-2 py-1">
							<p className="text-sm font-bold text-orange-500">{t("WARNING")}!</p>
							{warnings.map((warning) => (
								<p key={warning} className="text-sm text-orange-500">
									{warning}
								</p>
							))}
						</div>
					)}

					<button
						type="button"
						aria-label={t("UNDO") as string}
						title={t("UNDO") as string}
						disabled={!canUndo}
						onClick={undo}
						className="flex h-9 w-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
					>
						<Undo2 className="h-4 w-4" />
					</button>
					<button
						type="button"
						aria-label={t("shiftPlanner.redo") as string}
						title={t("shiftPlanner.redo") as string}
						disabled={!canRedo}
						onClick={redo}
						className="flex h-9 w-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
					>
						<Redo2 className="h-4 w-4" />
					</button>

					<span className="text-sm text-gray-700">
						{t("shiftPlanner.unsavedSummary", {
							created: diff.created.length,
							updated: diff.updated.length,
							removed: diff.removed.length,
						})}
					</span>

					<button
						type="button"
						disabled={errors.length > 0 || isSaving}
						onClick={handleSave}
						className="rounded bg-success px-6 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
					>
						{isSaving ? "..." : t("SAVE_CHANGES")}
					</button>
				</div>
			)}
		</div>
	);
};

export default JobMatchingTab;
